import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "./cn";

type Variant = "primary" | "ghost" | "subtle" | "danger" | "outline";
type Size = "sm" | "md" | "lg" | "icon";

const VARIANT: Record<Variant, string> = {
  primary:
    "text-white bg-[linear-gradient(135deg,#6366F1_0%,#8B5CF6_100%)] " +
    "shadow-[0_8px_24px_-12px_hsl(var(--accent)/0.7),inset_0_1px_0_hsl(0_0%_100%/0.2)] " +
    "hover:brightness-110 active:brightness-95",
  outline:
    "text-fg-strong bg-surface-2/40 border border-line-soft/80 hover:border-[hsl(var(--accent)/0.6)] hover:bg-surface-2/70",
  ghost:
    "text-fg-muted bg-transparent border border-transparent hover:bg-surface-2/60 hover:text-fg-strong",
  subtle:
    "text-fg-strong bg-surface-2/70 border border-line-soft/60 hover:bg-surface-3/80",
  danger:
    "text-white bg-rose-600 border border-rose-500/60 hover:bg-rose-500",
};

const SIZE: Record<Size, string> = {
  sm: "h-8 px-2.5 text-xs gap-1.5 rounded-lg",
  md: "h-9 px-3.5 text-sm gap-2 rounded-lg",
  lg: "h-11 px-5 text-sm font-medium gap-2 rounded-xl",
  icon: "h-9 w-9 grid place-items-center rounded-lg",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

/**
 * Design-system Button — gradient primary, glass ghost, subtle, danger.
 * - Hover lifts (-1px) with deeper shadow.
 * - Focus shows accent ring.
 * - Active scales down (gesture feedback).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "ghost", size = "md", className, loading, leftIcon, rightIcon, children, disabled, type, ...rest },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={isDisabled}
      className={cn(
        "relative inline-flex items-center justify-center font-medium tracking-tight",
        "transition-[transform,box-shadow,background,border-color,opacity] duration-150",
        "focus-ring",
        "active:translate-y-0 active:scale-[0.98]",
        "hover:-translate-y-[1px]",
        "disabled:pointer-events-none disabled:opacity-55 disabled:hover:translate-y-0",
        SIZE[size],
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});
